import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, CircularProgress, InputBase, Skeleton, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Search01Icon} from "@hugeicons/core-free-icons";
import InfiniteScroll from "react-infinite-scroll-component";
import {ConversationRepository} from "../../../repositories/ConversationRepository";
import {useDebounce} from "../../../hooks/useDebounce";
import {useFlowProtection} from "../../../hooks/useFlowProtection";
import {controlSx, PillSelect} from "../dashboard/components/Panel";
import {LiveDot} from "../dashboard/components/MetricCard";
import {formatStatus} from "../dashboard/components/conversationUi";
import {SupervisionListItem} from "./SupervisionListItem";
import {subProjectLabel} from "../../../utils/subProjects";
import {VERSION_TYPES} from "../SubProjectSelect";

const PAGE_SIZE = 25;

const VOICE_STATUSES = [
  "ACTIVE", "FINISHED", "SUCCESS", "REDIRECTED", "HANDOVER", "HANGUP", "MISSED_CALL", "FAILED", "USER_NOT_TALKING"
];
const CHAT_STATUSES = ["ACTIVE", "FINISHED", "FAILED"];
const DEFAULT_VERSION_TYPE = "PRODUCTION";

const isTestConversation = (conversation) => [
  conversation?.projectVersionType, conversation?.versionType, conversation?.conversationType, conversation?.type,
  conversation?.initParams?.PROJECT_VERSION_TYPE,
].some(value => String(value || "").toUpperCase() === "TEST");

// Calls where the caller gave no meaningful input; shown apart until resolved
const needsAttention = (conversation) =>
  (conversation.status === "USER_NOT_TALKING" || conversation.status === "MISSED_CALL") && !conversation.resolved;

const SkeletonRows = ({count}) => (
  <Stack gap={0.5} sx={{px: 0.5, py: 0.5}}>
    {Array.from({length: count}).map((_, index) => (
      <Stack key={index} direction={"row"} gap={1.25} sx={{px: 1, py: 1.25}}>
        <Skeleton variant={"circular"} width={36} height={36} sx={{flexShrink: 0}}/>
        <Box sx={{flex: 1}}>
          <Skeleton variant={"rounded"} width={"55%"} height={14} sx={{borderRadius: "6px"}}/>
          <Skeleton variant={"rounded"} width={"35%"} height={12} sx={{borderRadius: "6px", mt: 1}}/>
        </Box>
      </Stack>
    ))}
  </Stack>
);

const SectionTitle =({children, count}) => (
  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}
         sx={{position: "sticky", top: 0, zIndex: 1, bgcolor: "#fff", px: 1.5, pt: 1.5, pb: 0.75}}>
    <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: "#8A8AA0"}}>{children}</Typography>
    {count !== undefined && <Typography sx={{fontSize: 11, color: "#B4B4C3"}}>{count}</Typography>}
  </Stack>
);

// Conversations of the project for supervision: live ones first, then the ones needing attention, then the rest
// (infinite scroll). conversationUpdate: last conversation pushed over the websocket (ConversationResponseDto,
// sender masked); subProjectId / onChangeLanguage: the language filter ("" = every language).
// activeUpdate: last active-conversations count pushed, each one reloads the first page (see refreshFirstPage).
export const SupervisionList = ({
                                  projectId, subProjects, conversationType, subProjectId, onChangeLanguage,
                                  lookups, selectedId, onSelect, conversationUpdate, activeUpdate, activeCount, onOrderChange,
                                }) => {
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [projectVersionType, setProjectVersionType] = useState(DEFAULT_VERSION_TYPE);
  const searchDebounced = useDebounce(search, 400);
  const requestRef = useRef(0);

  const isVoice = conversationType !== "CHAT_BOT";
  const {subProjectOf, flowOf, tagsOf} = lookups;

  const query = useMemo(() => {
    const term = searchDebounced.trim();
    const isIdSearch = term.startsWith("#");
    return {
      projectId,
      subProjectId: subProjectId || undefined,
      type: conversationType,
      id: isIdSearch ? term.slice(1).trim() || undefined : undefined,
      caller: !isIdSearch ? term || undefined : undefined,
      status: status || undefined,
      projectVersionType: projectVersionType || DEFAULT_VERSION_TYPE,
    };
  }, [projectId, subProjectId, conversationType, searchDebounced, status, projectVersionType]);

  // a new unlock token (protected messages unlocked or locked again) reloads the list: senders are masked without it
  const {token: protectionToken} = useFlowProtection();

  const load = useCallback(async (pageToLoad) => {
    if (!projectId) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const response = await ConversationRepository.getConversations({...query, page: pageToLoad, size: PAGE_SIZE});
      if (requestId !== requestRef.current) return;
      const items = response?.data?.content || [];
      setTotal(response?.data?.totalElements || 0);
      setConversations(prev => {
        if (pageToLoad === 0) return items;
        const byId = new Map(prev.map(item => [item.id, item]));
        items.forEach(item => byId.set(item.id, item));
        return Array.from(byId.values());
      });
      setPage(pageToLoad);
    } catch (e) {
      console.error("Error fetching conversations:", e);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, query, protectionToken]);

  // first page again whenever the filters change (or the protection token)
  useEffect(() => {
    load(0);
  }, [load]);

  // The number of active conversations changed (a call started or ended): reload the first page and merge it on top,
  // keeping the pages already scrolled. Only one such reload runs at a time; changes that arrive while it is still
  // fetching are ignored.
  const activeRefreshRef = useRef(null);
  const queryRef = useRef(query);
  queryRef.current = query;
  const refreshFirstPage = () => {
    if (activeRefreshRef.current || !projectId) return;
    const controller = new AbortController();
    activeRefreshRef.current = controller;
    const requestQuery = queryRef.current;
    ConversationRepository.getConversations({...requestQuery, page: 0, size: PAGE_SIZE, signal: controller.signal})
      .then(response => {
        // the filters changed meanwhile: the reload they started wins
        if (queryRef.current !== requestQuery) return;
        const items = response?.data?.content || [];
        const ids = new Set(items.map(item => item.id));
        setTotal(response?.data?.totalElements || 0);
        setConversations(prev => [...items, ...prev.filter(item => !ids.has(item.id))]);
      })
      .catch(e => {
        if (e?.name !== "CanceledError" && e?.name !== "AbortError") console.error("Error refreshing conversations:", e);
      })
      .finally(() => {
        if (activeRefreshRef.current === controller) activeRefreshRef.current = null;
      });
  };
  useEffect(() => {
    if (!activeUpdate) return;
    if (conversationType && activeUpdate.conversationType && activeUpdate.conversationType !== conversationType) return;
    refreshFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUpdate]);
  useEffect(() => () => activeRefreshRef.current?.abort(), []);

  // Pushed conversation: update it in place (keeping the unmasked sender). A conversation not in the list yet is
  // never added from the push: the first page is loaded again, so the server's filters (version type, search,
  // status) decide whether it belongs (test conversations never do)
  useEffect(() => {
    if (!conversationUpdate?.id) return;
    const exists = conversations.some(item => item.id === conversationUpdate.id);
    if (exists) {
      setConversations(list => list.map(item => item.id === conversationUpdate.id
        ? {...item, ...conversationUpdate, sender: item.sender, senderName: item.senderName ?? conversationUpdate.senderName}
        : item));
      return;
    }
    refreshFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationUpdate]);

  const [active, attention, recent] = useMemo(() => {
    const visible = conversations.filter(item => !isTestConversation(item));
    return [
      visible.filter(item => item.status === "ACTIVE"),
      visible.filter(item => item.status !== "ACTIVE" && needsAttention(item)),
      visible.filter(item => item.status !== "ACTIVE" && !needsAttention(item)),
    ];
  }, [conversations]);

  useEffect(() => {
    onOrderChange?.([...active, ...attention, ...recent]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, attention, recent]);

  // one ticking clock for every live duration
  const hasActive = active.length > 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!hasActive) return;
    setNow(Date.now());
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [hasActive]);

  const renderItem = (conversation, withClock = false) => (
    <SupervisionListItem
      key={conversation.id}
      conversation={conversation}
      name={conversation.senderName || flowOf(conversation)?.phonebook?.[conversation.sender] || conversation.sender}
      language={subProjectOf(conversation)}
      tags={tagsOf(conversation)}
      selected={String(selectedId) === String(conversation.id)}
      isVoice={isVoice}
      now={withClock ? now : undefined}
      onClick={() => onSelect(conversation)}
    />
  );

  const languageOptions = [
    {value: "", label: "All sub projects"},
    ...(subProjects || []).map(item => ({value: String(item.id), label: subProjectLabel(item)})),
  ];
  const statusOptions = [
    {value: "", label: "All statuses"},
    ...(isVoice ? VOICE_STATUSES : CHAT_STATUSES).map(value => ({value, label: formatStatus(value)})),
  ];
  const versionOptions = VERSION_TYPES.map(item => ({value: item.value, label: item.label}));

  return (
    <Stack sx={{height: "100%", minHeight: 0}}>
      <Box sx={{px: 2, pt: 2.5, pb: 1.5}}>
        <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"} gap={1}>
          <Box>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>
              LIVE MONITORING
            </Typography>
            <Typography component={"h1"} sx={{fontSize: 20, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
              Supervision
            </Typography>
          </Box>
          <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{
            mt: 0.5, px: 1.25, py: 0.5, borderRadius: "999px", bgcolor: "#E8F7EF", color: "#1E8A55", fontSize: 12, fontWeight: 600,
          }}>
            <LiveDot/> {activeCount ?? active.length} active
          </Stack>
        </Stack>

        <Box component={"label"} sx={{...controlSx, mt: 2, width: "100%", boxSizing: "border-box"}}>
          {loading ? <CircularProgress size={15} sx={{color: "#8491F7"}}/> : <HugeiconsIcon icon={Search01Icon} size={16}/>}
          <InputBase
            placeholder={"Search caller or #id"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{"aria-label": "Search conversations"}}
            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
        </Box>
        <Stack direction={"row"} gap={1} flexWrap={"wrap"} sx={{mt: 1.25}}>
          <PillSelect value={subProjectId} options={languageOptions} onChange={onChangeLanguage}
                      placeholder={"Sub project"} ariaLabel={"Filter by sub project"}/>
          <PillSelect value={status} options={statusOptions} onChange={setStatus}
                      placeholder={"Status"} ariaLabel={"Filter by status"}/>
          <PillSelect value={projectVersionType} options={versionOptions} onChange={setProjectVersionType}
                      placeholder={"Version"} ariaLabel={"Filter by version"}/>
        </Stack>
      </Box>

      <Box id={"supervision-list"} sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 1, pb: 1}}>
        {active.length > 0 && (
          <Box>
            <SectionTitle count={active.length}>ACTIVE NOW</SectionTitle>
            {active.map(conversation => renderItem(conversation, true))}
          </Box>
        )}
        {attention.length > 0 && (
          <Box>
            <SectionTitle count={attention.length}>NEEDS ATTENTION</SectionTitle>
            {attention.map(conversation => renderItem(conversation))}
          </Box>
        )}
        <SectionTitle count={total}>{query.id || query.caller || query.status || query.projectVersionType !== DEFAULT_VERSION_TYPE ? "RESULTS" : "RECENT"}</SectionTitle>
        <InfiniteScroll
          dataLength={conversations.length}
          next={() => !loading && load(page + 1)}
          hasMore={conversations.length < total}
          loader={<SkeletonRows count={2}/>}
          scrollableTarget={"supervision-list"}
        >
          {recent.map(conversation => renderItem(conversation))}
        </InfiniteScroll>
        {loading && conversations.length === 0 && <SkeletonRows count={6}/>}
        {!loading && conversations.length === 0 && (
          <Typography sx={{py: 5, textAlign: "center", fontSize: 13, color: "#8A8AA0"}}>No conversations found</Typography>
        )}
      </Box>
    </Stack>
  );
};
